import { useEffect, useState } from 'react'
import { api } from '../../lib/api'

interface Profile {
  id: string
  name: string
  is_default: number
}

interface BulkPasswordChangeProps {
  siteId: string
  onClose: () => void
}

export function BulkPasswordChange({ siteId, onClose }: BulkPasswordChangeProps) {
  const [profiles, setProfiles] = useState<Profile[]>([])
  const [profileId, setProfileId] = useState('')
  const [scope, setScope] = useState<'all' | 'selected' | 'matching'>('all')
  const [matchFilter, setMatchFilter] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [updateOnvif, setUpdateOnvif] = useState(true)
  const [updateStored, setUpdateStored] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [deviceCount, setDeviceCount] = useState<number | null>(null)

  useEffect(() => {
    api.get<{ data: Profile[] }>('/provisioning-profiles')
      .then(res => {
        setProfiles(res.data)
        const defaultProfile = res.data.find(p => p.is_default === 1)
        if (defaultProfile) setProfileId(defaultProfile.id)
        else if (res.data.length > 0) setProfileId(res.data[0].id)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    api.get<{ data: unknown[]; total: number }>(`/sites/${siteId}/devices?limit=1`)
      .then(res => setDeviceCount(res.total))
      .catch(() => {})
  }, [siteId])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (newPassword !== confirmPassword) { setError('Passwords do not match'); return }
    if (!newPassword) { setError('New password is required'); return }
    if (!currentPassword) { setError('Current password is required'); return }
    if (!profileId) { setError('Select a profile'); return }

    setSaving(true)
    try {
      await api.post('/provisioning-jobs/bulk-password', {
        site_id: siteId,
        profile_id: profileId,
        scope,
        match_filter: scope === 'matching' ? matchFilter : undefined,
        current_password: currentPassword,
        new_password: newPassword,
        update_onvif: updateOnvif,
        update_stored: updateStored,
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create bulk password job')
    } finally { setSaving(false) }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '7px 10px', fontSize: 13,
    border: '1px solid var(--border)', borderRadius: 6,
    background: 'var(--bg)', color: 'var(--text)',
  }
  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }
  const radioRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button
          onClick={onClose}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 14, fontWeight: 500 }}
        >
          &larr; Back
        </button>
        <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)' }}>Bulk Password Change</h2>
      </div>

      <form onSubmit={handleSubmit} style={{
        maxWidth: 440, padding: 20,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-xs)',
      }}>
        {error && (
          <div style={{
            padding: '8px 12px', fontSize: 13, color: 'var(--err)',
            background: 'rgba(229,62,62,0.06)', borderRadius: 6, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Profile</label>
          <select style={inputStyle} value={profileId} onChange={e => setProfileId(e.target.value)}>
            {profiles.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Scope</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={radioRow}>
              <input type="radio" name="scope" checked={scope === 'all'} onChange={() => setScope('all')} />
              <span style={{ color: 'var(--text-secondary)' }}>
                All cameras on this site{deviceCount !== null ? ` (${deviceCount})` : ''}
              </span>
            </label>
            <label style={radioRow}>
              <input type="radio" name="scope" checked={scope === 'selected'} onChange={() => setScope('selected')} />
              <span style={{ color: 'var(--text-secondary)' }}>Selected cameras only</span>
            </label>
            <label style={radioRow}>
              <input type="radio" name="scope" checked={scope === 'matching'} onChange={() => setScope('matching')} />
              <span style={{ color: 'var(--text-secondary)' }}>Cameras matching:</span>
            </label>
            {scope === 'matching' && (
              <input
                style={{ ...inputStyle, marginLeft: 24, width: 'calc(100% - 24px)' }}
                value={matchFilter}
                onChange={e => setMatchFilter(e.target.value)}
                placeholder="Filter by label/hostname"
              />
            )}
          </div>
        </div>

        <div className="space-y-3" style={{ marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Current Password</label>
            <input type="password" style={inputStyle} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required />
          </div>
          <div>
            <label style={labelStyle}>New Password</label>
            <input type="password" style={inputStyle} value={newPassword} onChange={e => setNewPassword(e.target.value)} required />
          </div>
          <div>
            <label style={labelStyle}>Confirm New Password</label>
            <input type="password" style={inputStyle} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={updateOnvif} onChange={e => setUpdateOnvif(e.target.checked)} />
            <span style={{ color: 'var(--text-secondary)' }}>Also update ONVIF password</span>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={updateStored} onChange={e => setUpdateStored(e.target.checked)} />
            <span style={{ color: 'var(--text-secondary)' }}>Update stored credentials</span>
          </label>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 500,
              background: 'var(--bg)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text-secondary)',
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 600,
              background: 'var(--primary)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius)', cursor: 'pointer',
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? 'Changing...' : 'Change Passwords'}
          </button>
        </div>
      </form>
    </div>
  )
}
